// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable } from '@angular/core';
import { Column, DataRow } from '@lfx/shared/interfaces';

/**
 * Configuration mapping for metric types to their user profile field names
 */
export interface MetricUserProfileConfig {
  userProfileField: string;
  clickableColumns: string[];
}

/**
 * User profile data extracted from a row
 */
export interface UserProfileData {
  memberId?: string;
  memberDisplayName?: string;
  memberLogoUrl?: string;
}

/**
 * Service responsible for metric chart configuration and business logic
 * Handles clickable columns, user profile field mapping, and data extraction
 */
@Injectable({
  providedIn: 'root',
})
export class MetricChartConfigService {
  /**
   * Metric type configuration map
   */
  private readonly METRIC_USER_PROFILE_CONFIG: Record<string, MetricUserProfileConfig> = {
    maintainers: {
      userProfileField: 'displayName',
      clickableColumns: ['displayName']
    },
    contributors: {
      userProfileField: 'displayName',
      clickableColumns: ['displayName']
    },
    commit_activities: {
      userProfileField: 'committer',
      clickableColumns: ['committer']
    },
    pull_requests_opened: {
      userProfileField: 'committer',
      clickableColumns: ['committer']
    },
    avg_time_to_merge_prs: {
      userProfileField: 'contributor',
      clickableColumns: ['contributor']
    }
  };

  /**
   * Get configuration for a metric type
   */
  getMetricConfig(metricType: string): MetricUserProfileConfig | undefined {
    return this.METRIC_USER_PROFILE_CONFIG[metricType];
  }

  /**
   * Check if a column is clickable for a given metric type
   */
  isColumnClickable(metricType: string, columnProp: string): boolean {
    const config = this.getMetricConfig(metricType);
    if (!config) {
      return false;
    }
    return config.clickableColumns.includes(columnProp);
  }

  /**
   * Get the user profile field name for a metric type
   */
  getUserProfileField(metricType: string): string | undefined {
    const config = this.getMetricConfig(metricType);
    return config?.userProfileField;
  }

  /**
   * Extract user profile data from a data row
   * @param row - The data row containing user information
   * @param fieldName - The field name to extract extraData from
   * @returns User profile data or null if not found
   */
  extractUserProfileData(row: DataRow, fieldName: string): UserProfileData | null {
    const fieldData = row[fieldName] as { extraData?: Record<string, string> };
    const extraData = fieldData?.extraData;

    if (!extraData?.memberId) {
      return null;
    }

    return {
      memberId: extraData.memberId,
      memberDisplayName: extraData.memberDisplayName,
      memberLogoUrl: extraData.memberLogoUrl
    };
  }

  /**
   * Determine if a click event should open a user profile
   * @param metricType - The metric type
   * @param column - The column that was clicked
   * @returns True if user profile should be opened
   */
  shouldOpenUserProfile(metricType: string, column: Column): boolean {
    return this.isColumnClickable(metricType, column.prop);
  }
}

